import { useCallback, useEffect, useState } from 'react';
import { api } from '../api.js';
import { forgetLocally, restoreNotebook, restorePage } from '../db/db.js';
import { syncer } from '../sync/sync.js';
import { useApp } from '../state.js';
import TopBar from '../ui/TopBar.js';

interface TrashNotebook { id: string; title: string; color: string; deletedAt: number; pages: number }
interface TrashPage {
  id: string; title: string; pageDate: string; notebook: string | null;
  notebookDeleted: boolean; deletedAt: number; strokes: number;
}
interface TrashData { retentionDays: number; notebooks: TrashNotebook[]; pages: TrashPage[] }

function diasRestantes(deletedAt: number, retention: number): number {
  const passados = (Date.now() - deletedAt) / 86_400_000;
  return Math.max(0, Math.ceil(retention - passados));
}

export default function Trash(): JSX.Element {
  const showToast = useApp((s) => s.showToast);
  const [data, setData] = useState<TrashData | null>(null);
  const [confirm, setConfirm] = useState<{ entity: 'notebook' | 'page'; id: string; title: string } | null>(null);

  const load = useCallback(() => {
    void api.get<TrashData>('/api/trash').then(setData).catch(() => setData(null));
  }, []);
  useEffect(load, [load]);

  async function restore(entity: 'notebook' | 'page', id: string): Promise<void> {
    if (entity === 'notebook') await restoreNotebook(id);
    else await restorePage(id);
    await syncer.run();
    load();
    showToast('Restaurado.');
  }

  async function purge(): Promise<void> {
    if (!confirm) return;
    try {
      await api.del(`/api/trash/${confirm.entity}/${confirm.id}`);
      await forgetLocally(confirm.entity, confirm.id);
      showToast('Apagado de vez.');
    } catch {
      showToast('Não consegui apagar. Sem internet?');
    } finally {
      setConfirm(null);
      load();
    }
  }

  const vazia = data && !data.notebooks.length && !data.pages.length;

  return (
    <>
      <TopBar title="Lixeira" back="/" />
      <div className="content">
        <div className="settings">
          <p className="desc" style={{ margin: 0 }}>
            O que é apagado fica aqui por {data?.retentionDays ?? 30} dias e some sozinho depois.
            Restaurar devolve tudo: páginas, traços e texto.
          </p>

          {!data ? <p className="muted">Carregando…</p> : null}
          {vazia ? <p className="muted">A lixeira está vazia.</p> : null}

          {data?.notebooks.length ? (
            <section>
              <header><h2>Cadernos</h2></header>
              <div className="body">
                {data.notebooks.map((n) => (
                  <div className="trash-row" key={n.id}>
                    <span className="swatch" style={{ background: n.color, marginBottom: 0 }} />
                    <div className="grow">
                      <strong>{n.title || 'Sem nome'}</strong>
                      <p className="muted small" style={{ margin: 0 }}>
                        {n.pages} página(s) · some em {diasRestantes(n.deletedAt, data.retentionDays)} dia(s)
                      </p>
                    </div>
                    <button onClick={() => void restore('notebook', n.id)}>Restaurar</button>
                    <button
                      className="danger"
                      onClick={() => setConfirm({ entity: 'notebook', id: n.id, title: n.title })}
                    >
                      Apagar de vez
                    </button>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {data?.pages.length ? (
            <section>
              <header><h2>Páginas</h2></header>
              <div className="body">
                {data.pages.map((p) => (
                  <div className="trash-row" key={p.id}>
                    <div className="grow">
                      <strong>{p.title || 'Sem título'}</strong>
                      <p className="muted small" style={{ margin: 0 }}>
                        {[p.notebook, p.pageDate, `${p.strokes} traço(s)`].filter(Boolean).join(' · ')}
                        {' · '}some em {diasRestantes(p.deletedAt, data.retentionDays)} dia(s)
                      </p>
                      {/* Restaurar a página sem o caderno deixaria ela invisível na
                          biblioteca: o caminho certo é restaurar o caderno. */}
                      {p.notebookDeleted ? (
                        <p className="small" style={{ margin: '4px 0 0', color: 'var(--warn)' }}>
                          O caderno dela também está na lixeira — restaure o caderno.
                        </p>
                      ) : null}
                    </div>
                    {!p.notebookDeleted ? (
                      <button onClick={() => void restore('page', p.id)}>Restaurar</button>
                    ) : null}
                    <button
                      className="danger"
                      onClick={() => setConfirm({ entity: 'page', id: p.id, title: p.title || 'Sem título' })}
                    >
                      Apagar de vez
                    </button>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

        </div>
      </div>

      {confirm ? (
        <div className="sheet" onClick={(e) => { if (e.target === e.currentTarget) setConfirm(null); }}>
          <div>
            <h2 style={{ margin: 0 }}>Apagar &ldquo;{confirm.title}&rdquo; de vez?</h2>
            <p className="muted small" style={{ margin: 0 }}>
              Isto não tem volta. Os traços e o texto vão junto, no aparelho e no servidor.
            </p>
            <div className="row wrap">
              <button onClick={() => setConfirm(null)}>Cancelar</button>
              <span className="spacer" />
              <button className="danger" onClick={() => void purge()}>Apagar de vez</button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
